import { Layout } from '@/components/Layout';
import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useState, useRef } from 'react';
import { MdCloudDownload, MdDirectionsCar, MdExpandMore, MdFilterAlt, MdPersonAdd, MdToday } from 'react-icons/md';
import FilterPanel from '../components/filter-panel/FilterPanel';
import HomeTable from '../components/home-table/HomeTable';
import '../home-screens.styles.css';
import { useAuthenticationStore } from '@/store';
import { AuthenticationState, OnboardingStep } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { copyWith, GenericResponse, ListResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { X4bActivityDto } from '../../vehicle/engine/X4bActivityDto';
import { DataTableStateEvent, SortOrder } from 'primereact/datatable';
import { DateHelper } from '@/common/DateHelper';
import { useNavigate } from 'react-router-dom';
import { routeWithId } from '@/constants/routes';
import OnboardingStepsScreen from '@/modules/onboarding/views/OnboardingStepsScreen';
import OnboardingFinishScreen from '@/modules/onboarding/views/OnboardingFinishScreen';
import { GetActivityListRequest, X4bProgramDto } from '@/modules/vehicle/engine/ActivityTypes';
import { TextHeadlineExtraExtraLarge2 } from '@/components/text/Texts';
import DateInput from '@/components/inputs/DateInput';
import moment, { Moment } from 'moment';
import ActivitySearchAutocomplete from '@/components/search-bar/ActivitySearchAutocomplete';
import { DataTableValue } from 'primereact/datatable';
import clsx from 'clsx';
import { useOnboardingState } from '@/modules/onboarding/engine/onboarding.hooks';

const Home = () => {
	const navigate = useNavigate();

	// sorting states
	const [sort, setSort] = useState({ sortField: '', sortOrder: 0 });
	const [nameOrLocationFilter, setNameOrLocationFilter] = useState('');
	const [tableFilter, setTableFilter] = useState(''); // Used for actual table filtering
	const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
	const tableRef = useRef<any>(null);

	// filtering states
	const [emptyState, setEmptyState] = useState<'noData' | 'noSearchData' | 'noFilterData'>('noData');
	const [showFilterPanel, setShowFilterPanel] = useState(false);
	const [paginationFilter, setPaginationFilter] = useState({ Skip: 0, Take: 5 });
	const [dateFilter, setDateFilter] = useState<Moment | undefined>();
	const [statusFilter, setStatusFilter] = useState<null | string>(null);
	const [programsCheckedIds, setProgramsCheckedIds] = useState<null | number[]>(null);
	const [amountFilter, setAmountFilter] = useState<null | number>(0);
	// Currently not used because we apply filters immediately
	const [filterList, setSideFilter] = useState<string[]>([]);

	const { stepsQ } = useOnboardingState();

	const programsQ = useQuery({
		queryKey: ['Home.Programs'],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bProgramDto>>>(ENDPOINTS['/api/X4bProgram/GetProgramList'], {});
			setProgramsCheckedIds(result.data.Data.Items.map(i => {
				return i.Id;
			}));
			return result.data.Data;
		}
	});

	const makeListRequest = () => {
		return {
			Skip: paginationFilter.Skip,
			Take: paginationFilter.Take,
			SortField: sort.sortField,
			SortOrder: sort.sortOrder,
			Filters: [{ Name: "NameOrLocation", Value: tableFilter }],
			Date: DateHelper.prepareDateToStore(dateFilter?.toDate()),
			Status: statusFilter,
			ProgramIds: programsCheckedIds,
			Amount: amountFilter
		} as GetActivityListRequest;
	}

	const activitiesQ = useQuery({
		queryKey: ['Home.GetActivityList', paginationFilter, sort, tableFilter, dateFilter, statusFilter, programsCheckedIds, amountFilter],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bActivityDto>>>(ENDPOINTS['/api/X4bActivity/GetActivityList'], makeListRequest());
			return result.data.Data;
		},
		enabled: programsQ.isSuccess,
	});

	// Note: We'll fetch autocomplete results on-demand in the component, not using react-query
	// to avoid unnecessary API calls

	const handleProgramCheckboxChange = (id: number) => {
		if (!programsCheckedIds) setProgramsCheckedIds([]);
		if (programsCheckedIds!.includes(id)) {
			setProgramsCheckedIds(programsCheckedIds!.filter((checkedId) => checkedId !== id));
		} else {
			setProgramsCheckedIds([...programsCheckedIds!, id]);
		}
	};

	const toggleFilterPanel = () => setShowFilterPanel(!showFilterPanel);

	const handleActivitySelect = (text: string) => {
		setPaginationFilter({ Skip: 0, Take: paginationFilter.Take });
		// Update the table filter to trigger a search
		setTableFilter(text);
	};

	useEffect(() => {
		const hasSearch = tableFilter.trim() !== '';
		const hasFilters = (statusFilter != null) ||
			(programsCheckedIds != null && programsCheckedIds.length !== programsQ.data?.TotalRecords) ||
			(amountFilter != 0);

		if (hasFilters) setEmptyState('noFilterData');
		else if (hasSearch) setEmptyState('noSearchData');
		else setEmptyState('noData');
		//console.log(hasSearch, hasFilters);
	}, [tableFilter, statusFilter, programsCheckedIds, programsQ.data?.TotalRecords, amountFilter]);

	var sideFiltersNumber = 0;
	if (statusFilter != null) sideFiltersNumber++;
	if (programsCheckedIds != null && programsCheckedIds.length != programsQ.data?.TotalRecords) sideFiltersNumber++;
	if (amountFilter != 0) sideFiltersNumber++;

	const isOnboardingStepsFinished = (stepsStr: string | undefined) => {
		if (!stepsStr) return false;
		const steps = stepsStr.split(",").map(s => s.trim()).filter(i => i);
		return steps.length >= 5;
	};

	const isOnboardingFinished = (stepsStr: string | undefined) => {
		if (!stepsStr) return false;
		const steps = stepsStr.split(",").map(s => s.trim()).filter(i => i);
		return steps.includes(OnboardingStep[OnboardingStep.Finished]);
	};

	const showOnboardingSteps = !stepsQ.isLoading && !isOnboardingFinished(stepsQ.data?.Steps)
		&& !isOnboardingStepsFinished(stepsQ.data?.Steps);
	const showOnboardingFinish = !stepsQ.isLoading && !isOnboardingFinished(stepsQ.data?.Steps)
		&& isOnboardingStepsFinished(stepsQ.data?.Steps);
	const showOnboardingHome = stepsQ.isLoading || isOnboardingFinished(stepsQ.data?.Steps);

	const { user } = useAuthenticationStore((state: AuthenticationState) => state);

	return (
		<>
			{showOnboardingSteps && (<OnboardingStepsScreen stepsQ={stepsQ} />)}
			{showOnboardingFinish && (<OnboardingFinishScreen />)}

			{showOnboardingHome && (
				<Layout>
					<div className="flex h-full">
						<div>
							{stepsQ.isLoading ? (
								<div className="skeleton-container">
									<Skeleton
										width="384px"
										height="20px"
										className="skeleton"
									/>
									<Skeleton
										width="368px"
										height="32px"
										className="skeleton"
									/>
									<div className="skeleton-row">
										<Skeleton
											width="198px"
											height="48px"
											className="skeleton"
										/>
										<Skeleton
											width="198px"
											height="48px"
											className="skeleton"
										/>
									</div>
									<Skeleton
										width="170px"
										height="36px"
										className="skeleton"
									/>
								</div>
							) : (
								<>
									<p className="company-name">Para Xpress Technologies Services Inc.</p>
									<TextHeadlineExtraExtraLarge2 text={`Good Morning, ${user.firstName} ${user.lastName}`} className="text-primary" />

									<div className="action-buttons">
										<RoundedFilledButton
											id="ride"
											className="with-icon-button h-12"
											icon={MdDirectionsCar}
											iconColor="text-secondary"
											label="Arrange a ride"
											onClick={() => {
												navigate(routeWithId('/concierge/rides/:id', 0));
											}}
										/>
										<RoundedFilledButton
											id="add-people"
											className="with-icon-button  h-12"
											icon={MdPersonAdd}
											iconColor="text-secondary"
											trailingIcon={MdExpandMore}
											label="Add people"
											onClick={() => {
												navigate('/people');
											}}
										/>
									</div>

									<div>
										<h3 className="activities-title">
											All Activities
										</h3>
									</div>
								</>
							)}

							<div className="top-bar">
								{stepsQ.isLoading ? (
									<div className="skeleton-flex">
										<Skeleton className="skeleton-item" />
										<div className="button-group">
											<RoundedFilledButton
												id="filter"
												className="with-icon-button"
												icon={MdFilterAlt}
												label='Filter'
												onClick={toggleFilterPanel}
												width="110px"
											/>
											<RoundedFilledButton
												id="export"
												className="with-icon-button-primary"
												icon={MdCloudDownload}
												label="Export"
												iconColor="text-onPrimary"
												onClick={() => { }}
												width="123px"
											/>
										</div>
										<Skeleton className="skeleton-item" />
									</div>
								) : (
									<>
										<div className="search-date-container">
											<ActivitySearchAutocomplete
												onSelect={handleActivitySelect}
												placeholder="Search by name or location"
												className="w-[360px] tablet:!w-auto tablet:!flex-1 tablet:!min-w-0 h-[56px]"
												value={nameOrLocationFilter}
												onChange={setNameOrLocationFilter}
											/>

											<DateInput
												value={dateFilter}
												onChange={setDateFilter}
												variant="outlined"
												icon={(<MdToday size={24} className="text-onSurfaceVariant" />)}
												iconCls="h-10 w-10 p-2 rounded-[100px] bg-[#D5D8DB] pointer-events-none top-2 right-1"
												labelBgColor="bg-surface"
												containerCls="w-[312px] tablet:w-[220px] tablet:!flex-none"
												calendarProps={{
													minDate: moment().year(2025).startOf('year'),
													maxDate: moment().year(2025).startOf('year').add(20, 'years'),
												}}
											/>
										</div>
										<div className="button-group">
											<RoundedFilledButton
												id="filter"
												className={clsx(
													"with-icon-button",
													sideFiltersNumber !== 0 && "border-2 border-solid border-orange-600"
												)}
												icon={MdFilterAlt}
												label={`Filter${sideFiltersNumber == 0 ? '' : `(${sideFiltersNumber})`}`}
												onClick={toggleFilterPanel}
												width="110px"
											/>
											<RoundedFilledButton
												id="export"
												className="with-icon-button-primary"
												icon={MdCloudDownload}
												label="Export"
												iconColor="text-onPrimary"
												onClick={async () => {
													const response = await axiosInstance.post(
														ENDPOINTS['/api/X4bActivity/ExportActivityList'],
														makeListRequest(),
														{ responseType: 'blob' }
													);
													// Create a blob from the response
													const blob = new Blob([response.data], { type: 'text/csv' });
													// Create a link element
													const url = window.URL.createObjectURL(blob);
													const link = document.createElement('a');
													link.href = url;
													link.setAttribute('download', 'activity-list.csv');
													document.body.appendChild(link);
													link.click();
													link.parentNode?.removeChild(link);
													window.URL.revokeObjectURL(url);
												}}
												width="123px"
											/>
										</div>
									</>
								)}
							</div>

							<HomeTable
								ref={tableRef}
								value={activitiesQ.data?.Items}
								rowsPerPage={5}
								emptyState={emptyState}
								loading={stepsQ.isLoading || activitiesQ.isLoading}
								dataKey="Id"
								paginator={activitiesQ.data?.Items?.length ?? 0 > 0}
								first={paginationFilter.Skip}
								rows={paginationFilter.Take}
								lazy
								totalRecords={activitiesQ.data?.TotalRecords}
								rowsPerPageOptions={[5, 10, 25, 50]}
								onPage={(event: DataTableStateEvent) => {
									setPaginationFilter(copyWith(paginationFilter, { Skip: event.first }));
								}}
								onRowsChange={(rows: number) => {
									setPaginationFilter(copyWith(paginationFilter, { Skip: 0, Take: rows }));
								}}
								sortField={sort.sortField}
								sortOrder={sort.sortOrder as SortOrder}
								onSort={(e: DataTableStateEvent) => {
									setSort({
										sortField: e.sortField,
										sortOrder: e.sortOrder!,
									});
								}}
								expandedActivityId={selectedActivityId}
								onExpandedActivityChange={setSelectedActivityId}
							/>
						</div>
						<FilterPanel
							show={showFilterPanel}
							onClose={() => setShowFilterPanel(false)}
							statusList={['OnTrip', 'Completed', 'Cancelled']}
							statusSelected={statusFilter}
							setStatusSelected={setStatusFilter}
							programsCheckedIds={programsCheckedIds}
							handleProgramCheckboxChange={handleProgramCheckboxChange}
							setSideFilter={setSideFilter}
							programsQ={programsQ}
							amount={amountFilter}
							onAmountChange={(am: number) => {
								setAmountFilter(am);
							}}
							onFilterClear={() => {
								setStatusFilter(null);
								setProgramsCheckedIds(programsQ.data!.Items.map(i => {
									return i.Id;
								}));
								setAmountFilter(0);
							}}
						/>
					</div>
				</Layout>
			)}
		</>
	);
};

export default Home;
